'use client';

import { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { useIsMobile } from './useIsMobile';

const SCROLL_LOCK_CLASS = '--scroll-lock';

type ScrollLockContextValue = {
    lock: () => void;
    unlock: () => void;
};

const ScrollLockContext = createContext<ScrollLockContextValue | null>(null);

export function ScrollLockProvider({ children }: { children: React.ReactNode }) {
    const countRef = useRef(0);
    const isMobile = useIsMobile();
    const savedScrollY = useRef(0);

    const applyLock = useCallback(() => {
        if (typeof document === 'undefined') return;
        if (isMobile) {
            savedScrollY.current = window.scrollY;
            const html = document.documentElement;
            const body = document.body;
            html.style.overflow = 'hidden';
            html.style.touchAction = 'none';
            body.style.position = 'fixed';
            body.style.top = `-${savedScrollY.current}px`;
            body.style.left = '0';
            body.style.right = '0';
            body.style.overflow = 'hidden';
            body.style.touchAction = 'none';
        } else {
            document.documentElement.classList.add(SCROLL_LOCK_CLASS);
        }
    }, [isMobile]);

    const removeLock = useCallback(() => {
        if (typeof document === 'undefined') return;
        const html = document.documentElement;
        const body = document.body;
        if (body.style.position === 'fixed') {
            const y = savedScrollY.current;
            html.style.overflow = '';
            html.style.touchAction = '';
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.overflow = '';
            body.style.touchAction = '';
            window.scrollTo({ top: y, left: 0, behavior: 'instant' });
        }
        body.style.overflow = '';
        html.classList.remove(SCROLL_LOCK_CLASS);
    }, []);

    const lock = useCallback(() => {
        countRef.current += 1;
        if (countRef.current === 1) applyLock();
    }, [applyLock]);

    const unlock = useCallback(() => {
        if (countRef.current > 0) countRef.current -= 1;
        if (countRef.current === 0) removeLock();
    }, [removeLock]);

    useEffect(() => {
        return () => {
            if (countRef.current > 0) {
                countRef.current = 0;
                removeLock();
            }
        };
    }, [removeLock]);

    return (
        <ScrollLockContext.Provider value={{ lock, unlock }}>
            {children}
        </ScrollLockContext.Provider>
    );
}

export function useScrollLock(): ScrollLockContextValue {
    const ctx = useContext(ScrollLockContext);
    if (!ctx) {
        throw new Error('useScrollLock must be used within ScrollLockProvider');
    }
    return ctx;
}
